import { motion } from 'framer-motion';
import { Bookmark, Heart, MoreHorizontal, X } from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge, IconButton } from '@/components/ui';
import { formatTimeAgo, getThemeLabel } from '@/features/feed/utils';
import type { FeedItem } from '@/types';

interface PublicContentCardProps {
  item: FeedItem;
  isBlurred: boolean;
  onInteractionAttempt: () => void;
}

export const PublicContentCard = memo(function PublicContentCard({
  item,
  isBlurred,
  onInteractionAttempt,
}: PublicContentCardProps) {
  const { t } = useTranslation();
  const { content, tier } = item;
  const isPrimary = tier === 'primary';

  const handleCardClick = () => {
    if (isBlurred) {
      onInteractionAttempt();
    }
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      layout
      className={`relative rounded-lg p-6 transition-shadow bg-white ${
        isPrimary ? 'shadow-md' : 'shadow-sm'
      } ${isBlurred ? 'cursor-pointer' : ''}`}
      onClick={handleCardClick}
    >
      {/* Blur overlay for locked cards */}
      {isBlurred && (
        <div className="absolute inset-0 z-10 rounded-lg overflow-hidden">
          {/* Gradient blur effect */}
          <div className="absolute inset-0 backdrop-blur-[12px] bg-white/60" />

          {/* Lock indicator */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-white/90 rounded-full px-4 py-2 shadow-lg flex items-center gap-2">
              <span className="text-[#E94E1B] font-serif text-lg">B</span>
              <span className="text-[#888185] text-sm font-medium">
                {t('publicFeed.unlockToRead', 'Entre para ler')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Card content */}
      <div className={isBlurred ? 'select-none' : ''}>
        {/* Theme Tag */}
        <Badge variant="theme" className="mb-3">
          {getThemeLabel(content.theme, t)}
        </Badge>

        {/* Headline */}
        {isBlurred ? (
          <h2
            className={`font-serif font-semibold text-[#2E2A23] leading-tight ${
              isPrimary ? 'text-xl md:text-2xl' : 'text-lg md:text-xl'
            }`}
          >
            {content.title}
          </h2>
        ) : (
          <Link to={`/preview/${content.id}`}>
            <h2
              className={`font-serif font-semibold text-[#2E2A23] leading-tight hover:text-[#E94E1B] transition-colors ${
                isPrimary ? 'text-xl md:text-2xl' : 'text-lg md:text-xl'
              }`}
            >
              {content.title}
            </h2>
          </Link>
        )}

        {/* Summary */}
        <p
          className={`mt-3 text-[#888185] leading-relaxed ${
            isPrimary ? 'line-clamp-3' : 'line-clamp-2'
          }`}
        >
          {content.summary}
        </p>

        {/* Metadata */}
        <div className="mt-4 flex items-center gap-2 text-sm text-[#888185]">
          <span className="font-medium">{content.author}</span>
          <span>·</span>
          <span>{formatTimeAgo(content.publishedAt, t)}</span>
        </div>

        {/* Disabled Actions */}
        <div className="mt-4 flex items-center gap-1">
          <IconButton
            icon={Heart}
            size="md"
            active={false}
            onClick={onInteractionAttempt}
            className="text-[#888185] cursor-not-allowed"
            aria-label={t('feed.actions.like')}
          />
          <IconButton
            icon={Bookmark}
            size="md"
            active={false}
            onClick={onInteractionAttempt}
            className="text-[#888185] cursor-not-allowed"
            aria-label={t('feed.actions.save')}
          />
          <IconButton
            icon={X}
            size="md"
            onClick={onInteractionAttempt}
            className="text-[#888185] cursor-not-allowed"
            aria-label={t('feed.actions.dismiss')}
          />
          <IconButton
            icon={MoreHorizontal}
            size="md"
            onClick={onInteractionAttempt}
            className="text-[#888185] cursor-not-allowed"
            aria-label={t('feed.actions.more')}
          />
        </div>
      </div>
    </motion.article>
  );
});
